import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/compat';
import { fetchApiGraphQL } from '@/utils/fetcher';
import { setUnreadMessageIndicator } from '../Login/unread-indicator';
import { hasValidToken } from '../Store/tokenstore';
import {
  GET_ME_APPLICATIONS_AND_MESSAGES,
  SEND_USER_TO_EMPLOYEE_MESSAGE,
  MARK_MESSAGE_AS_READ,
  PERFORM_ADVERT_ACTION,
} from './advert-application-messages.query';
import {
  MeResponse,
  UserAdvertApplication,
  MessageThread,
  TransformedMessage,
  SendMessageResult,
  MarkMessageAsReadResult,
  ApplicationAction,
} from './AdvertApplicationMessagesTypes';

export interface UseAdvertApplicationMessagesResult {
  threads: MessageThread[];
  loading: boolean;
  error: string | null;
  isLoggedIn: boolean;
  selectedThreadId: string | null;
  setSelectedThreadId: (id: string | null) => void;
  getThreadById: (id: string) => MessageThread | undefined;
  sendMessage: (advertId: string, text: string) => Promise<boolean>;
  sending: boolean;
  sendError: string | null;
  markMessageAsRead: (messageId: number) => Promise<void>;
  performAction: (advertId: string, action: ApplicationAction, reason?: string) => Promise<void>;
}

// Helper function to transform raw message data
export function transformMessage(
  message: MeResponse['data']['me']['messages'][0]
): TransformedMessage {
  return {
    id: message.id,
    text: message.text,
    created: message.created,
    isFromUser: message.fromUserId !== null,
    isFromEmployee: message.fromEmployeeId !== null,
    senderId: message.fromEmployeeId ?? message.fromUserId,
  };
}

/* Same shape as the rest of the site: "<address>, <postal code> <city>".
   Falls back to the advert title so a thread is never left unlabelled. */
function residenceAddress(advert: UserAdvertApplication): string {
  const residence = advert.residenceAdvert?.residence;
  const parts = [residence?.address, `${residence?.postalCode ?? ''} ${residence?.postalCity ?? ''}`.trim()];

  return parts.filter((part) => part).join(', ') || (advert.residenceAdvert?.title ?? '');
}

// Helper function to create a preview from text
function createPreview(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
}

/** When the application reached its current status - the API keeps one date per step. */
function statusDate(advert: UserAdvertApplication): string | null {
  switch (advert.status) {
    case 'Created':
      return advert.applicationDate;
    case 'Invited':
      return advert.invitedDate;
    case 'ConfirmedOpenHouse':
    case 'RejectedOpenHouse':
      return advert.confirmedOpenHouseDate ?? advert.updatedDate;
    case 'AwaitingInterest':
      return advert.interestRequestSentDate;
    case 'Interested':
    case 'NotInterested':
      return advert.interestedDate;
    case 'Rejected':
      return advert.declinedDate ?? advert.updatedDate;
    default:
      return advert.updatedDate;
  }
}

export function useAdvertApplicationMessages() {
  const isLoggedIn = hasValidToken();
  const [data, setData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Fetch data on mount
  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchApiGraphQL<MeResponse>(
          GET_ME_APPLICATIONS_AND_MESSAGES,
          {}
        );
        setData(response);
      } catch (err) {
        setError('Failed to load messages. Please try again later.');
        console.error('Error fetching application messages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isLoggedIn]);

  // The header dot lives outside preact - keep it in step as threads are read,
  // otherwise it sticks around until the next page load.
  useEffect(() => {
    if (data) {
      setUnreadMessageIndicator(data.data.me.unreadMessages.length > 0);
    }
  }, [data]);

  // Transform raw data into message threads
  const threads: MessageThread[] = useMemo(() => {
    if (!data) return [];

    const { adverts, messages, unreadMessages } = data.data.me;

    // Create a map of advertId to advert info
    const advertMap = new Map<string, UserAdvertApplication>();
    adverts.forEach((advert) => {
      if (advert.residenceAdvert) {
        advertMap.set(advert.residenceAdvert.id, advert);
      }
    });

    // Create a Set of unread message IDs for quick lookup
    const unreadMessageIds = new Set<number>();
    unreadMessages.forEach((msg) => {
      unreadMessageIds.add(msg.id);
    });

    // Create a map of advertId to unread message count
    const unreadCountMap = new Map<string, number>();
    unreadMessages.forEach((msg) => {
      const currentCount = unreadCountMap.get(msg.advertId) ?? 0;
      unreadCountMap.set(msg.advertId, currentCount + 1);
    });

    // Create a map of advertId to messages
    const messageMap = new Map<string, TransformedMessage[]>();
    messages.forEach((msg) => {
      const transformed = transformMessage(msg);
      // Mark if this message is in the unread list
      transformed.isUnread = unreadMessageIds.has(msg.id);
      const currentMessages = messageMap.get(msg.advertId) ?? [];
      messageMap.set(msg.advertId, [...currentMessages, transformed]);
    });

    // Build threads from the maps
    const builtThreads: MessageThread[] = [];

    // Iterate through adverts to ensure we have all threads (even without messages)
    adverts.forEach((advert) => {
      if (!advert.residenceAdvert) return;

      const advertId = advert.residenceAdvert.id;
      const threadMessages = messageMap.get(advertId) ?? [];

      // Sort messages by created date
      threadMessages.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());

      const lastMessage = threadMessages[threadMessages.length - 1];

      builtThreads.push({
        advertId,
        residenceAddress: residenceAddress(advert),
        status: advert.status,
        statusDate: statusDate(advert),
        availableActions: advert.availableActions ?? [],
        selectedOpenHouseStart: advert.selectedOpenHouseStart ?? null,
        messages: threadMessages,
        unreadCount: unreadCountMap.get(advertId) ?? 0,
        lastMessageDate: lastMessage?.created ?? '',
        lastMessagePreview: lastMessage ? createPreview(lastMessage.text) : '',
        lastMessageIsFromUser: lastMessage?.isFromUser,
        hasUnread: (unreadCountMap.get(advertId) ?? 0) > 0,
        propertyUrlPath: advert.residenceAdvert?.residence?.property?.urlPath ?? null,
      });
    });

    // Sort threads by last message date (newest first)
    builtThreads.sort((a, b) => {
      if (!a.lastMessageDate) return 1;
      if (!b.lastMessageDate) return -1;
      return new Date(b.lastMessageDate).getTime() - new Date(a.lastMessageDate).getTime();
    });

    return builtThreads;
  }, [data]);

  // Arriving from an email action link (?advertId=...) opens that advert's thread, so the
  // user lands on the conversation they just answered on. Only on the first load - a refresh
  // after sending would otherwise drag them back here. Guids travel with either casing.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || threads.length === 0) return;

    const advertId = new URLSearchParams(document.location.search).get('advertId');
    const match = advertId
      ? threads.find((thread) => thread.advertId.toLowerCase() === advertId.toLowerCase())
      : undefined;

    preselected.current = true;
    if (match) setSelectedThreadId(match.advertId);
  }, [threads]);

  // Helper to get a specific thread by ID
  const getThreadById = (id: string): MessageThread | undefined => {
    return threads.find((t) => t.advertId === id);
  };

  // Which pane shows on mobile follows from whether a thread is selected - the
  // island does that with CSS, so there is no view state to flip here.
  const handleSelectThread = (threadId: string | null) => {
    setSelectedThreadId(threadId);
    setSendError(null);
  };

  // Send a message to an employee
  const sendMessage = useCallback(
    async (advertId: string, text: string): Promise<boolean> => {
      if (!text.trim()) {
        setSendError('Message cannot be empty');
        return false;
      }

      setSending(true);
      setSendError(null);

      try {
        const variables: Record<string, unknown> = {
          advertId,
          text,
        };

        await fetchApiGraphQL<SendMessageResult>(
          SEND_USER_TO_EMPLOYEE_MESSAGE,
          variables
        );

        // After sending, refresh the data to get the new message
        const refreshVariables: Record<string, unknown> = {};
        const refreshResponse = await fetchApiGraphQL<MeResponse>(
          GET_ME_APPLICATIONS_AND_MESSAGES,
          refreshVariables
        );
        setData(refreshResponse);

        return true;
      } catch (err) {
        setSendError('Failed to send message. Please try again.');
        console.error('Error sending message:', err);
        return false;
      } finally {
        setSending(false);
      }
    },
    [data, setData, setSending, setSendError]
  );

  // Throws on failure so the confirm overlay can show it. The refetch brings the new
  // status/actions and the thread message the API posts for the action.
  const performAction = useCallback(
    async (advertId: string, action: ApplicationAction, reason?: string): Promise<void> => {
      await fetchApiGraphQL<{ data: { performAdvertAction: boolean } }>(PERFORM_ADVERT_ACTION, {
        residenceAdvertId: advertId,
        action,
        reason: reason?.trim() || null,
      });
      setData(await fetchApiGraphQL<MeResponse>(GET_ME_APPLICATIONS_AND_MESSAGES, {}));
    },
    []
  );

  // Track timers for marking messages as read
  const readTimers = useRef<Map<number, number>>(new Map());

  // Mark a message as read after 2 seconds of being visible
  const markMessageAsRead = useCallback(
    async (messageId: number): Promise<void> => {
      // Clear any existing timer for this message
      if (readTimers.current.has(messageId)) {
        clearTimeout(readTimers.current.get(messageId));
        readTimers.current.delete(messageId);
      }

      // Set a timer to mark as read after 2 seconds
      const timer = window.setTimeout(async () => {
        try {
          const variables: Record<string, unknown> = { messageId };
          await fetchApiGraphQL<MarkMessageAsReadResult>(
            MARK_MESSAGE_AS_READ,
            variables
          );
          
          // Optimistically update the local state to mark message as read
          setData((prevData) => {
            if (!prevData) return prevData;
            
            // Create a new copy of the data with the message marked as read
            const newMessages = prevData.data.me.messages.map((msg) => {
              if (msg.id === messageId) {
                return { ...msg };
              }
              return msg;
            });
            
            // Remove from unreadMessages
            const newUnreadMessages = prevData.data.me.unreadMessages.filter(
              (msg) => msg.id !== messageId
            );
            
            return {
              ...prevData,
              data: {
                ...prevData.data,
                me: {
                  ...prevData.data.me,
                  messages: newMessages,
                  unreadMessages: newUnreadMessages,
                },
              },
            };
          });
          
          readTimers.current.delete(messageId);
        } catch (err) {
          console.error('Error marking message as read:', err);
        }
      }, 2000); // 2 seconds

      readTimers.current.set(messageId, timer);
    },
    [setData]
  );

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      readTimers.current.forEach((timer) => clearTimeout(timer));
      readTimers.current.clear();
    };
  }, []);

  return {
    threads,
    loading,
    error,
    isLoggedIn,
    selectedThreadId,
    setSelectedThreadId: handleSelectThread,
    getThreadById,
    sendMessage,
    sending,
    sendError,
    markMessageAsRead,
    performAction,
  };
}
